"use client";
// PlausibleAnalytics.tsx — self-hosted Plausible pageview tracking (cookieless).
// script.manual.js + one explicit pageview per route, so a bearer token in the
// path (/invite/<token>, /s/<token>) is redacted by flare's redactTokenPath
// before it leaves the browser; script.js would send location.href raw.
import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { redactTokenPath } from "@/lib/flare";

type Plausible = ((event: string, options?: { u?: string }) => void) & { q?: unknown[][] };
declare global {
  interface Window {
    plausible?: Plausible;
  }
}

export function PlausibleAnalytics() {
  const pathname = usePathname();
  useEffect(() => {
    // The queue stub the manual script drains on load, so a pageview fired
    // before the script arrives is not lost.
    const plausible: Plausible =
      window.plausible ?? Object.assign((...args: unknown[]) => void (plausible.q ??= []).push(args), {});
    window.plausible = plausible;
    plausible("pageview", { u: redactTokenPath(window.location.href) });
  }, [pathname]);

  return (
    <Script
      defer
      // Without this the script is opaque to window.onerror: a throw inside it reaches
      // every listener as the literal "Script error." with no stack and no filename,
      // which is unactionable and which flare's ingest therefore drops. The origin
      // already sends `access-control-allow-origin: *`, so this costs nothing.
      crossOrigin="anonymous"
      data-domain="vantage.crystalprism.io"
      src="https://plausible.crystalprism.io/js/script.manual.js"
      strategy="afterInteractive"
    />
  );
}
